import React, { ReactNode } from 'react';
import { productListViewed } from 'utils/metrics';
import { Item } from 'types';
import PaginationNav from 'components/PaginationNav';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import SalesCalloutGroomsFree from 'components/SalesCalloutGroomsFree';
import SummaryCard from 'components/SummaryCard';
import CollectionListItem from './CollectionListItem';
import Link from 'next/link';

const PER_PAGE_DEFAULT = 24;
const PER_PAGE_BUNDLE = 50;

type BundleGroups = 'allure' | 'suit' | 'tux';

interface GroupedBundles {
  allure: Item[];
  suit: Item[];
  tux: Item[];
}

interface Props {
  products: Item[];
  activeCategory: {
    name: string;
  };
  hasActiveFilter: boolean;
  showCalloutCards: boolean;
  itemType: 'bundle' | 'product';
}

interface State {
  currentPage: number;
  perPage: number;
}

class CollectionList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      currentPage: 1,
      perPage: PER_PAGE_DEFAULT,
    };
    this.incrementPage = this.incrementPage.bind(this);
    this.decrementPage = this.decrementPage.bind(this);
  }

  componentDidMount() {
    this.trackProductListViewed();
    this.setItemsPerPage();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.products !== this.props.products) {
      this.setState({
        currentPage: 1,
      });
    }

    this.setItemsPerPage();
  }

  setItemsPerPage = () => {
    if (this.props.itemType === 'bundle' && this.state.perPage !== PER_PAGE_BUNDLE) {
      this.setState({ perPage: PER_PAGE_BUNDLE });
    }

    if (this.props.itemType !== 'bundle' && this.state.perPage === PER_PAGE_BUNDLE) {
      this.setState({ perPage: PER_PAGE_DEFAULT });
    }
  };

  incrementPage = () => {
    this.scrollToTop();
    this.setState(
      (state) => ({ currentPage: state.currentPage + 1 }),
      () => {
        this.trackProductListViewed();
      }
    );
  };

  decrementPage = () => {
    this.scrollToTop();
    this.setState(
      (state) => ({ currentPage: state.currentPage - 1 }),
      () => {
        this.trackProductListViewed();
      }
    );
  };

  scrollToTop = () => {
    document.getElementById('collection-nav')?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  trackProductListViewed = () => productListViewed(this.getCurrentPageProducts(), this.props.activeCategory!.name);

  getCurrentPageProducts = () =>
    this.props.products.slice(
      this.state.currentPage * this.state.perPage - this.state.perPage,
      this.state.currentPage * this.state.perPage
    );

  groupedBundles = () =>
    this.getCurrentPageProducts().reduce(
      (acc: GroupedBundles, val: Item) => {
        const isAllureItem = val.display_name && val.display_name?.search('Allure') > -1;
        const isNonAllureSuit = !isAllureItem && val.type === 'suit';
        const isNonAllureTux = !isAllureItem && val.type === 'tuxedo';

        if (isAllureItem) {
          acc['allure'] = [...acc['allure'], val];
        }

        if (isNonAllureSuit) {
          acc['suit'] = [...acc['suit'], val];
        }

        if (isNonAllureTux) {
          acc['tux'] = [...acc['tux'], val];
        }

        return acc;
      },
      { allure: [], suit: [], tux: [] }
    );

  renderCombinedProductsAndCallouts = (currentPageProducts: GroupedBundles) => {
    const groups: BundleGroups[] = ['suit', 'allure', 'tux'];

    let callouts = [
      <div className="collection-page-sales-callouts col-span-6 md:col-span-4" key="hto-sales-callout">
        <div className="flex aspect-ratio-5by7">
          <SalesCalloutSwatches ctaPageName="collection" />
        </div>
      </div>,
      <div className="collection-page-sales-callouts col-span-6 md:col-span-4" key="swatches-sales-callout">
        <div className="flex aspect-ratio-5by7">
          <SalesCalloutHTO ctaPageName="collection" style={{ flexGrow: 1 }} />
        </div>
      </div>,
      <div className="collection-page-sales-callouts col-span-6 md:col-span-4" key="groomsfree-sales-callout">
        <div className="flex aspect-ratio-5by7">
          <SalesCalloutGroomsFree ctaPageName="collection" />
        </div>
      </div>,
    ];

    const summaryCards = {
      suit: (
        <div className="col-span-12 lg:col-span-4 lg:col-start-1" key="suit-summary-card" id="suits">
          <div className="flex lg:aspect-ratio-5by7">
            <SummaryCard title="Men's Suits">
              Stylish and comfortable. A good suit works for a formal wedding or casual affair. Choose your suit color
              and customize the accessories to build your perfect look.
            </SummaryCard>
          </div>
        </div>
      ),
      tux: (
        <div className="col-span-12 lg:col-span-4 lg:col-start-1" key="tux-summary-card" id="tuxedos">
          <div className="flex lg:aspect-ratio-5by7">
            <SummaryCard title="Men's Tuxedos">
              Classic and timeless. A tuxedo makes a statement that can’t be matched. Design the dream look for you and
              your wedding party.
            </SummaryCard>
          </div>
        </div>
      ),
      allure: (
        <div className="col-span-12 lg:col-span-4 lg:col-start-1" key="allure-summary-card" id="vows">
          <div className="flex lg:aspect-ratio-5by7">
            <SummaryCard
              logo={
                <img
                  src="https://gentux.imgix.net/1699282182_GTLogoAllureBridals.png?w=250&dpr=2"
                  width={250}
                  alt="The Allure Bridals Logo"
                />
              }
            >
              The Generation Tux and Allure Bridals{' '}
              <Link href="/vowscollection">
                <a className="tracker-text-collection-vows_collection-230412-104843">Vows Collection</a>
              </Link>
              . Four new suits designed in bold colors and constructed with comfort features so you look great, and feel
              great.
            </SummaryCard>
          </div>
        </div>
      ),
    };

    const summaryWithBundles = groups.reduce((acc: ReactNode[], val: BundleGroups) => {
      if (currentPageProducts[val].length > 0) {
        acc.push(summaryCards[val]);

        currentPageProducts[val].map((p: Item, index) => {
          // add sales callout every 5th item
          if ((index + 1) % 5 === 0) {
            const callout = callouts.shift();
            if (callout) {
              acc.push(callout);
            }
          }

          acc.push(
            <CollectionListItem key={`${p.category}-${p.id}`} product={p} isBundle={this.props.itemType === 'bundle'} />
          );
        });
      }

      return acc;
    }, []);

    return summaryWithBundles;
  };

  renderProducts = () => {
    const currentPageProducts = this.getCurrentPageProducts().map((p) => (
      <CollectionListItem key={`${p.category}-${p.id}`} product={p} isBundle={this.props.itemType === 'bundle'} />
    ));

    if (this.props.showCalloutCards && !this.props.hasActiveFilter) {
      const groupedBundles = this.groupedBundles();
      return this.renderCombinedProductsAndCallouts(groupedBundles);
    }

    return currentPageProducts;
  };

  render() {
    return (
      <>
        <div id="item-list" style={{ position: 'relative' }}>
          <div id="collection-nav" className="absolute right-0 translate-y-[-150%] transform sm:translate-y-[-200%]">
            <PaginationNav
              currentPage={this.state.currentPage}
              perPage={this.state.perPage}
              items={this.props.products}
              decrementPage={this.decrementPage}
              incrementPage={this.incrementPage}
            />
          </div>

          <div className="row mb-32 gap-y-16 gap-x-8 xs:gap-x-16 sm:gap-y-32 sm:gap-x-32">{this.renderProducts()}</div>

          <PaginationNav
            currentPage={this.state.currentPage}
            perPage={this.state.perPage}
            items={this.props.products}
            decrementPage={this.decrementPage}
            incrementPage={this.incrementPage}
          />
        </div>
      </>
    );
  }
}

export default CollectionList;
